/**
 * The findings on the day, all of them, read only, under the map as the Main
 * board places them. The day's hard ones are also listed first, in the left
 * column, by `NeedsAttention`, which renders each with this file's
 * `FindingItem` and groups them as this panel does.
 *
 * Three rules shape it, each from the spec or the API:
 *
 * - Severity is a word ("Hard", "Warn") in a Badge whose tone repeats it,
 *   never colour alone (WCAG 1.4.1).
 * - The counts are the API's `hard` and `warn`, never a count of the rows on
 *   screen. They cover what the API returned for this day: the day's dated
 *   findings and every undated one.
 * - An undated finding is about a farm's gap between collections or the whole
 *   run (the validator dates everything about one day), and cannot be placed
 *   on a day, so the API returns it for every day. It gets a group of its own
 *   rather than being mixed in with the day's.
 *
 * Every source the API names is listed with its status (`FindingSources`, in
 * the day's explanation at the foot of the page), so a check that did not run
 * (`route_check` is `not_available` until the TomTom route check is merged)
 * is said in plain words instead of looking like a check that found nothing.
 */
import { Badge, Section } from "@depicomnlbv/platform-ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type { Day, Finding, Findings, FindingSource } from "../api";
import { tripTitle } from "../trips";
import { FailureSentence } from "./Failure";

/** The API's source names, in words. The name itself is the API's key and
 *  is never shown: an unknown source is "Another check". */
const SOURCE_NAMES: Record<string, string> = {
  validate: "Operating rules",
  route_check: "TomTom route check",
};

const sourceName = (name: string) => SOURCE_NAMES[name] ?? "Another check";

/** What a dispatcher is told about a check that is not running. The API's
 *  `detail` for this status is written for developers ("planner.route_check
 *  is not on this branch yet"), so it is not shown; this sentence is. */
const NOT_RUNNING: Record<string, string> = {
  route_check: "The TomTom road check is not running yet, so there are no road findings.",
};

function Source({ source }: { source: FindingSource }) {
  if (source.status === "not_available") {
    return (
      <li>
        {NOT_RUNNING[source.name] ?? "A check that is not running yet has added no findings."}
      </li>
    );
  }
  // The source's `detail` is written for developers, as for a source that is
  // not running, so it is not shown either.
  return (
    <li>
      <strong>{sourceName(source.name)}</strong>: checked.
    </li>
  );
}

/** Hard before warn; otherwise in the order the API sent them. */
function bySeverity(findings: Finding[]): Finding[] {
  return [...findings].sort(
    (a, b) => (a.severity === "hard" ? 0 : 1) - (b.severity === "hard" ? 0 : 1),
  );
}

/** One finding: severity in words, the rule, the text, and the trip it is
 *  about as a link that selects it. ``source`` false leaves out which check
 *  found it, for a list that is about the day rather than the checks. */
export function FindingItem({
  finding,
  tripName,
  source = true,
}: {
  finding: Finding;
  tripName: string | null;
  source?: boolean;
}) {
  const hard = finding.severity === "hard";
  return (
    <li className="finding">
      <span className="finding__head">
        <Badge tone={hard ? "critical" : "high"}>{hard ? "Hard" : "Warn"}</Badge>{" "}
        <span className="finding__rule">{finding.rule_name}</span>
      </span>{" "}
      <span className="finding__text">{finding.text}</span>{" "}
      <span className="finding__meta">
        {finding.route_id !== null ? (
          tripName ? (
            <Link to={{ search: `?trip=${finding.route_id}` }}>{tripName}</Link>
          ) : (
            // A trip id the day does not hold: said, not linked to nothing.
            <span>Route {finding.route_id}</span>
          )
        ) : null}
        {finding.route_id !== null && source ? " · " : null}
        {source ? sourceName(finding.source) : null}
      </span>
    </li>
  );
}

function Group({
  id,
  title,
  findings,
  nameOf,
}: {
  id: string;
  title: string;
  findings: Finding[];
  nameOf: (routeId: number | null) => string | null;
}) {
  if (findings.length === 0) return null;
  return (
    <>
      <h4 className="findings__group" id={id}>
        {title}
      </h4>
      <ul aria-labelledby={id} className="findings" role="list">
        {bySeverity(findings).map((finding, index) => (
          <FindingItem
            finding={finding}
            key={`${finding.rule}-${index}`}
            tripName={nameOf(finding.route_id)}
          />
        ))}
      </ul>
    </>
  );
}

/** Which checks the findings come from, for the day's explanation at the
 *  foot of the page. A check that did not run is said in words, so it does
 *  not look like a check that found nothing. */
export function FindingSources({ sources }: { sources: FindingSource[] }) {
  return (
    <ul aria-label="Checks behind these findings" className="sources" role="list">
      {sources.map((source) => (
        <Source key={source.name} source={source} />
      ))}
    </ul>
  );
}

export function FindingsPanel({
  day,
  query,
  runId,
  date,
}: {
  day: Day;
  query: UseQueryResult<Findings>;
  runId: number;
  date: string;
}) {
  const nameOf = (routeId: number | null) => tripTitle(day.trips, routeId);

  if (query.isPending) return <Section loading title="Findings" />;
  if (query.isError) {
    return (
      <Section error={<FailureSentence context={{ runId, date }} error={query.error} />} title="Findings" />
    );
  }
  const { hard, warn, findings } = query.data;
  const onTrips = findings.filter((f) => f.route_id !== null);
  const onDay = findings.filter((f) => f.route_id === null && f.date !== null);
  const onPlan = findings.filter((f) => f.date === null && f.route_id === null);
  return (
    <Section
      aside={
        // The tone repeats the number and never replaces it: nought is
        // neutral, so a clean day does not look like an alarm.
        <span className="findings__counts">
          <Badge tone={hard > 0 ? "critical" : "neutral"}>Hard: {hard}</Badge>{" "}
          <Badge tone={warn > 0 ? "high" : "neutral"}>Warn: {warn}</Badge>
        </span>
      }
      empty={findings.length === 0 ? "No findings on this day." : undefined}
      title="Findings"
    >
      <Group findings={onTrips} id="findings-trips" nameOf={nameOf} title="On this day's trips" />
      <Group findings={onDay} id="findings-day" nameOf={nameOf} title="Elsewhere on this day" />
      <Group findings={onPlan} id="findings-plan" nameOf={nameOf} title="About the whole plan" />
    </Section>
  );
}
